import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import {
  BookOpen, Users, BarChart2, Plus, FileText, Loader2,
  ArrowRight, BookMarked, TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  getTeachingCourses,
  getCourseStudents,
  type Course,
  type EnrolledStudentRow,
} from "@/api/lms/courses";

type StudentRow = EnrolledStudentRow & { courseTitle: string; courseId: string };

function displayStudentName(s: EnrolledStudentRow): string {
  const fn = s.user.profile?.firstName ?? "";
  const ln = s.user.profile?.lastName ?? "";
  const full = `${fn} ${ln}`.trim();
  return full || s.user.email;
}

export default function TeacherDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teaching, setTeaching] = useState<Course[]>([]);
  const [studentsByCourse, setStudentsByCourse] = useState<Record<string, EnrolledStudentRow[]>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const courses = await getTeachingCourses();
        if (cancelled) return;
        setTeaching(courses);

        const entries = await Promise.all(
          courses.map(async (c) => {
            try {
              const list = await getCourseStudents(c.id);
              return [c.id, list] as [string, EnrolledStudentRow[]];
            } catch {
              return [c.id, []] as [string, EnrolledStudentRow[]];
            }
          }),
        );
        if (cancelled) return;
        const map: Record<string, EnrolledStudentRow[]> = {};
        entries.forEach(([id, list]) => {
          map[id] = list;
        });
        setStudentsByCourse(map);
      } catch (e: unknown) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load teacher dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allStudentRows: StudentRow[] = useMemo(() => {
    const rows: StudentRow[] = [];
    teaching.forEach((c) => {
      (studentsByCourse[c.id] ?? []).forEach((s) => {
        rows.push({ ...s, courseTitle: c.title, courseId: c.id });
      });
    });
    rows.sort((a, b) => (b.enrolledAt ?? "").localeCompare(a.enrolledAt ?? ""));
    return rows;
  }, [teaching, studentsByCourse]);

  const flatStudents = useMemo(() => allStudentRows.slice(0, 40), [allStudentRows]);
  const uniqueStudentIds = useMemo(
    () => new Set(allStudentRows.map((s) => s.userId)),
    [allStudentRows],
  );
  const totalEnrollments = allStudentRows.length;
  const publishedCourses = teaching.filter((c) => c.isPublished).length;

  const stats = useMemo(
    () => [
      {
        label: "My Courses",
        value: String(teaching.length),
        sub: `${publishedCourses} published`,
        icon: BookOpen,
        color: "bg-primary/10 text-primary",
      },
      {
        label: "Unique Students",
        value: String(uniqueStudentIds.size),
        sub: "Across all courses",
        icon: Users,
        color: "bg-blue-50 text-blue-600",
      },
      {
        label: "Total Enrollments",
        value: String(totalEnrollments),
        sub: "All course seats",
        icon: BarChart2,
        color: "bg-emerald-50 text-emerald-600",
      },
    ],
    [uniqueStudentIds.size, teaching.length, totalEnrollments, publishedCourses],
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dash-page">
          <div className="dash-empty-state mt-20">
            <Loader2 className="animate-spin mb-2" size={24} />
            <span className="text-sm font-medium">Loading teacher hub…</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page">

        {/* Page Header */}
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Teacher Hub</h1>
            <p className="dash-page-subtitle">
              {error ??
                (teaching.length === 0
                  ? "You're not assigned to any courses yet."
                  : "Your assigned courses and enrolled students.")}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" asChild>
              <Link to="/teacher/create-exam">
                <Plus size={14} className="mr-1.5" /> Create Exam
              </Link>
            </Button>
            <Button size="sm" asChild>
              <Link
                to={
                  teaching[0]
                    ? `/teacher/courses/${teaching[0].id}/structure`
                    : "/teacher/courses"
                }
              >
                <FileText size={14} className="mr-1.5" /> Lessons & Structure
              </Link>
            </Button>
          </div>
        </div>

        {/* KPI Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((s, i) => (
            <KCard key={i} className="dash-stat-card">
              <div className="flex items-start justify-between">
                <div className={`dash-stat-icon ${s.color}`}>
                  <s.icon size={18} />
                </div>
                <TrendingUp size={14} className="text-muted-foreground/40 mt-0.5" />
              </div>
              <div>
                <p className="dash-stat-value">{s.value}</p>
                <p className="dash-stat-label mt-1">{s.label}</p>
              </div>
              <p className="text-[11px] text-muted-foreground/70 font-medium">{s.sub}</p>
            </KCard>
          ))}
        </div>

        {/* Courses Grid */}
        <div className="mb-8">
          <div className="dash-section-header">
            <h2 className="dash-section-title">Your Courses</h2>
            <Link to="/teacher/courses" className="dash-section-link flex items-center gap-1">
              All courses <ArrowRight size={12} />
            </Link>
          </div>
          {teaching.length === 0 ? (
            <div className="dash-empty-state">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <BookMarked size={28} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground text-lg mb-2">No assigned courses</h3>
              <p className="text-sm text-muted-foreground">Ask an admin to assign you to a course.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {teaching.map((c) => {
                const n = studentsByCourse[c.id]?.length ?? 0;
                return (
                  <Link key={c.id} to={`/teacher/courses/${c.id}/structure`}>
                    <KCard hoverable className="p-5 h-full flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2 flex-1">
                          {c.title}
                        </h3>
                        <span
                          className={`dash-badge shrink-0 ${
                            c.isPublished
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {c.isPublished ? "Published" : "Draft"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-auto">
                        <Users size={12} />
                        <span>
                          {n} student{n === 1 ? "" : "s"}
                        </span>
                      </div>
                      <p className="text-xs text-primary font-semibold flex items-center gap-1">
                        Open structure <ArrowRight size={11} />
                      </p>
                    </KCard>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Students Table */}
        <div>
          <div className="dash-section-header">
            <h2 className="dash-section-title">Students</h2>
            <Link to="/teacher/students" className="dash-section-link flex items-center gap-1">
              Full list <ArrowRight size={12} />
            </Link>
          </div>
          <KCard className="overflow-hidden">
            <table className="w-full dash-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Enrolled</th>
                </tr>
              </thead>
              <tbody>
                {flatStudents.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="p-4">
                      <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                        <p className="text-sm font-medium">No enrollments in your courses yet.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  flatStudents.map((s) => (
                    <tr key={`${s.courseId}-${s.id}`}>
                      <td>
                        <p className="text-sm font-medium text-foreground leading-tight">
                          {displayStudentName(s)}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{s.user.email}</p>
                      </td>
                      <td>
                        <Link
                          className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors"
                          to={`/teacher/courses/${s.courseId}/structure`}
                        >
                          {s.courseTitle}
                        </Link>
                      </td>
                      <td className="text-[11px] text-muted-foreground">
                        {s.enrolledAt ? new Date(s.enrolledAt).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </KCard>
        </div>

      </div>
    </DashboardLayout>
  );
}
